import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { ReceiptModal } from "@features/cx/shared/components/ReceiptModal";
import { GenericTable } from "@shared/components/GenericTable";
import { toast } from "@shared/hooks/use-toast";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { TableColumn } from "@shared/types/table";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { FileText, Printer, X } from "lucide-react";
import React, { useMemo, useState } from "react";
import { useCreditNoteDetail } from "../../hooks";

interface CreditNoteDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  creditNoteId: number;
}

interface CreditApplication {
  id: string;
  dateApplied: string;
  type: "Bill" | "Service";
  reference: string;
  description: string;
  amount: string;
  appliedBy: string;
}

export const CreditNoteDetailsModal: React.FC<CreditNoteDetailsModalProps> = ({
  isOpen,
  onClose,
  creditNoteId,
}) => {
  const { data: creditNoteData } = useCreditNoteDetail(creditNoteId);
  const [isReceiptModalOpen, setIsReceiptModalOpen] = useState(false);
  const currency = useCurrency();

  // Consumer details fields
  const consumerFields = [
    {
      label: "Consumer Name",
      value:
        creditNoteData?.consumer?.firstName +
        " " +
        creditNoteData?.consumer?.lastName,
    },
    {
      label: "Account Number",
      value: creditNoteData?.consumer?.consumerNo,
    },
    {
      label: "Email",
      value: creditNoteData?.consumer?.email,
      isHoverable: true,
      className: "min-w-0",
    },
    {
      label: "Phone",
      value: creditNoteData?.consumer?.contactNumber,
    },
  ];

  // Reference information fields
  const referenceFields = [
    {
      label: "Linked Payment",
      value: (
        <span className="text-blue-600 font-medium">
          {creditNoteData?.linkedEntity?.referenceNo}
        </span>
      ),
    },
    {
      label: "Reason",
      value: creditNoteData?.creditNoteReason,
    },
    {
      label: "Created By",
      value: creditNoteData?.createdUserRemoteName,
    },
    {
      label: "Created On",
      value: creditNoteData?.createdOn,
    },
    {
      label: "Remarks",
      value: creditNoteData?.remarks,
    },
  ];

  // Financial details fields
  const financialFields = [
    {
      label: "Credit Amount",
      value: (
        <span className="text-2xl font-bold text-gray-900">
          {creditNoteData?.amount}
        </span>
      ),
    },
    {
      label: "Remaining Balance",
      value: (
        <span className="text-2xl font-bold text-green-600">
          {creditNoteData?.financialDetails?.remainingBalance}
        </span>
      ),
      tooltip: "Credit balance available for use",
    },
    {
      label: "Applied Amount",
      value: (
        <span className="text-2xl font-bold text-blue-600">
          {creditNoteData?.financialDetails?.appliedAmount}
        </span>
      ),
      tooltip: "Total amount already applied",
    },
    {
      label: "Usage Percentage",
      value: (
        <span className="text-2xl font-bold text-gray-900">
          {creditNoteData?.financialDetails?.usagePercentage}
        </span>
      ),
      tooltip: "Percentage of credit utilized",
    },
  ];

  // Action buttons
  const actionButtons = [
    {
      icon: <Printer className="w-4 h-4" />,
      label: "Print",
      onClick: () => setIsReceiptModalOpen(true),
    },
  ];

  // Define table columns for GenericTable
  const tableColumns: TableColumn<CreditApplication>[] = useMemo(
    () => [
      {
        key: "dateApplied",
        header: "Date Applied",
        type: "text",
        sortable: false,
      },
      {
        key: "type",
        header: "Type",
        type: "custom",
        sortable: false,
        render: (value: string) => (
          <Badge
            variant={value === "Bill" ? "default" : "secondary"}
            className={
              value === "Bill"
                ? "bg-blue-100 text-blue-800"
                : "bg-cyan-100 text-cyan-800"
            }
          >
            {value === "Bill" ? "📄 Bill" : "🔧 Service"}
          </Badge>
        ),
      },
      {
        key: "reference",
        header: "Reference",
        type: "custom",
        sortable: false,
        render: (value: string) => (
          <span className="text-sm font-medium text-blue-600">{value}</span>
        ),
      },
      {
        key: "description",
        header: "Description",
        type: "text",
        sortable: false,
      },
      {
        key: "amount",
        header: "Amount",
        type: "custom",
        sortable: false,
        render: (value: string) => (
          <span className="text-sm font-semibold text-gray-900">{value}</span>
        ),
      },
      {
        key: "appliedBy",
        header: "Applied By",
        type: "text",
        sortable: false,
      },
    ],
    [],
  );

  const receiptData = useMemo(() => {
    if (!creditNoteData) return null;

    const recordedDateTime = creditNoteData.issuedOn?.split(" ") || [];
    console.log(creditNoteData);

    return {
      transactionId: creditNoteData.creditNoteNumber || "",
      totalAmount: creditNoteData.amount || "0.00",
      currency,
      consumerName: `${creditNoteData.consumer?.firstName || ""} ${
        creditNoteData.consumer?.lastName || ""
      }`.trim(),
      consumerAccount: creditNoteData.consumer?.consumerNo || "N/A",
      paymentDate: creditNoteData.paymentDate || "",
      reason: creditNoteData.creditNoteReason || "N/A",
      linkedPayment: creditNoteData.linkedEntity?.referenceNo || "N/A",
      breakdown: {
        creditAmount: creditNoteData.amount || "0.00",
        remainingBalance:
          creditNoteData.financialDetails?.remainingBalance || "0.00",
        appliedAmount: creditNoteData.financialDetails?.appliedAmount || "0.00",
      },
    };
  }, [creditNoteData, currency]);

  const receiptLeftColumn = useMemo(() => {
    if (!receiptData) return [];
    return [
      {
        label: "Consumer",
        value: receiptData.consumerName,
        subValue: receiptData.consumerAccount,
      },
      {
        label: "Reason",
        value: receiptData.reason,
      },
    ];
  }, [receiptData]);

  const receiptRightColumn = useMemo(() => {
    if (!receiptData) return [];
    return [
      {
        label: "Issued Date",
        value: receiptData.paymentDate,
        subValue: receiptData.paymentTime,
      },
      {
        label: "Linked Payment",
        value: receiptData.linkedPayment,
        valueClassName: "font-medium text-blue-600",
      },
    ];
  }, [receiptData]);

  const receiptBreakdownItems = useMemo(() => {
    if (!receiptData) return [];

    return [
      {
        label: "Credit Amount",
        value: `${receiptData.currency} ${receiptData.breakdown.creditAmount}`,
        className: "text-gray-600",
        valueClassName: "font-medium text-gray-900",
      },
      {
        label: "Applied Amount",
        value: `${receiptData.currency} ${receiptData.breakdown.appliedAmount}`,
        className: "text-gray-600",
        valueClassName: "font-medium text-gray-900",
      },
      {
        label: "Remaining Balance",
        value: `${receiptData.currency} ${receiptData.breakdown.remainingBalance}`,
        className: "font-semibold text-gray-900 pt-3 border-t border-gray-200",
        valueClassName: "font-bold text-green-600 text-base",
        highlight: true,
      },
    ];
  }, [receiptData]);

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <DialogHeader className="border-b pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-gray-600" />
                <DialogTitle className="text-xl font-semibold">
                  Credit Note Details
                </DialogTitle>
              </div>
            </div>
          </DialogHeader>

          {/* Content */}
          <div className="space-y-6 py-4">
            {/* Credit Note Header */}
            <div className="bg-blue-50 rounded-lg p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="w-8 h-8 text-blue-600" />
                <div>
                  <p className="text-lg font-semibold text-gray-900">
                    Credit Note {creditNoteData?.creditNoteNumber}
                  </p>
                  <p className="text-sm text-gray-600">
                    Issued on {creditNoteData?.issuedOn}
                  </p>
                </div>
              </div>
              <Badge className="bg-blue-600 text-white hover:bg-blue-600">
                {creditNoteData?.paymentReceivedStatus}
              </Badge>
            </div>

            {/* Consumer Details */}
            <div className="border rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">
                Consumer Details
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {consumerFields.map((field, index) => (
                  <ValueLabel key={index} {...field} />
                ))}
              </div>
            </div>

            {/* Reference Information */}
            <div className="border rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">
                Reference Information
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {referenceFields.map((field, index) => (
                  <ValueLabel key={index} {...field} />
                ))}
              </div>
            </div>

            {/* Financial Details */}
            <div className="border rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">
                Financial Details
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {financialFields.map((field, index) => (
                  <ValueLabel key={index} {...field} />
                ))}
              </div>
            </div>

            {/* Credit Applications */}
            <div className="border rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">
                Credit Applications
              </h3>
              <GenericTable<CreditApplication>
                data={creditNoteData?.creditApplications || []}
                columns={tableColumns}
                rowKey="id"
                loading={false}
                emptyMessage="No credit applications found"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="border-t pt-4 flex justify-between">
            <div className="flex gap-2">
              {actionButtons.map((button, index) => (
                <Button
                  key={index}
                  variant="outline"
                  size="sm"
                  onClick={button.onClick}
                  className="flex items-center gap-2"
                >
                  {button.icon}
                  {button.label}
                </Button>
              ))}
            </div>
            <Button onClick={onClose}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>
      {receiptData && (
        <ReceiptModal
          isOpen={isReceiptModalOpen}
          onClose={() => setIsReceiptModalOpen(false)}
          title="Credit Note Receipt"
          receiptId={receiptData.transactionId}
          navigateToDashboard="/cx/payments"
          maxWidth="max-w-2xl"
          showPrintButton={true}
          showDownloadButton={true}
          onPrintComplete={() => {
            toast({
              title: "Print Successful",
            });
          }}
          onDownloadComplete={() => {
            toast({
              title: "Download Successful",
            });
          }}
        >
          {/* Inline Receipt Content */}
          <div className="space-y-6">
            {/* Header Section - Total Credit Amount */}
            <div className="text-center py-6 bg-gray-50 rounded-lg border-t-4 border-blue-500">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                Total Credit Amount
              </p>
              <p className="text-3xl font-bold text-gray-900">
                {receiptData.currency} {receiptData.totalAmount}
              </p>
            </div>

            {/* Credit Note Details Grid */}
            <div className="grid grid-cols-2 gap-x-8 gap-y-6">
              {/* Left Column */}
              <div className="space-y-4">
                {receiptLeftColumn.map((item, index) => (
                  <div key={index}>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                      {item.label}
                    </p>
                    <p
                      className={
                        item.valueClassName || "font-semibold text-gray-900"
                      }
                    >
                      {item.value}
                    </p>
                    {item.subValue && (
                      <p className="text-sm text-gray-600">{item.subValue}</p>
                    )}
                  </div>
                ))}
              </div>

              {/* Right Column */}
              <div className="space-y-4">
                {receiptRightColumn.map((item, index) => (
                  <div key={index}>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                      {item.label}
                    </p>
                    <p className="font-semibold text-gray-900">{item.value}</p>
                    {item.subValue && (
                      <p className="text-sm text-gray-600">{item.subValue}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Credit Breakdown Section */}
            <div className="bg-gray-50 rounded-lg p-5 space-y-3">
              <h3 className="font-semibold text-gray-900 text-sm mb-4">
                Credit Breakdown
              </h3>

              <div className="space-y-3">
                {receiptBreakdownItems.map((item, index) => (
                  <div
                    key={index}
                    className={`flex justify-between items-center text-sm ${item.className}`}
                  >
                    <span>{item.label}</span>
                    <span className={item.valueClassName}>{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </ReceiptModal>
      )}
    </>
  );
};
